// shell.jsx — root route switcher + persistent tab bar

// Phase 17: IndexedDB photo storage bootstrap.
// Initializes the Dexie `liftlog_photos` DB on app mount and runs the
// idempotent migration of v1.1 base64/SVG-data-URI photos into IDB blobs.
// Mounts null — side-effect-only component, sibling of ThemeInjector.

import React from 'react';
import { WelcomeScreen } from './auth.jsx';
import { initPhotosDB, migratePhotosFromLocalStorage } from './photos.jsx';
import { HomeScreen } from './home.jsx';
import { useApp } from './store.jsx';
import { LL, LLIcon, applyTheme, useWide } from './tokens.jsx';
import { WorkoutSummary } from './summary.jsx';
import { ActiveWorkout } from './active.jsx';
import { useAnySheetOpen } from './sheet_history.js';
import { useSyncBridge } from './sync.js';
import { useNativeBridge } from './native.js';
import { useSharedLinkPending } from './share_link.js';

// Secondary screens load as separate chunks (smaller first paint); they are
// prefetched on idle right after boot so navigating to them is instant, and
// the service worker precaches every chunk for offline use.
const loaders = {
  library: () => import('./library.jsx'),
  detail: () => import('./exercise_detail.jsx'),
  insights: () => import('./insights.jsx'),
  coach: () => import('./coach.jsx'),
  programs: () => import('./programs.jsx'),
  settings: () => import('./screens_wired.jsx'),
  settingsSub: () => import('./settings_sub.jsx'),
  body: () => import('./body.jsx'),
  workout: () => import('./workout.jsx'),
  sync: () => import('./sync_settings.jsx'),
  device: () => import('./device_settings.jsx'),
};
const lazyNamed = (key, name) => React.lazy(() => loaders[key]().then(m => ({ default: m[name] })));
const ExerciseLibrary = lazyNamed('library', 'ExerciseLibrary');
const ExerciseEditor = lazyNamed('library', 'ExerciseEditor');
const ExerciseDetail = lazyNamed('detail', 'ExerciseDetail');
const InsightsScreen = lazyNamed('insights', 'InsightsScreen');
const CoachScreen = lazyNamed('coach', 'CoachScreen');
const AISettings = lazyNamed('coach', 'AISettings');
const ProgramsScreen = lazyNamed('programs', 'ProgramsScreen');
const ProgramEditor = lazyNamed('programs', 'ProgramEditor');
const SettingsScreenWired = lazyNamed('settings', 'SettingsScreenWired');
const BodyScreen = lazyNamed('body', 'BodyScreen');
const WorkoutPreviewScreen = lazyNamed('workout', 'WorkoutPreviewScreen');
const AdvancedSettings = lazyNamed('settingsSub', 'AdvancedSettings');
const CSVImport = lazyNamed('settingsSub', 'CSVImport');
const ExportScreen = lazyNamed('settingsSub', 'ExportScreen');
const ImportJSON = lazyNamed('settingsSub', 'ImportJSON');
const NotifsSettings = lazyNamed('settingsSub', 'NotifsSettings');
const RemindersSettings = lazyNamed('settingsSub', 'RemindersSettings');
const ProgressionSettings = lazyNamed('settingsSub', 'ProgressionSettings');
const RestTimerSettings = lazyNamed('settingsSub', 'RestTimerSettings');
const ThemeSettings = lazyNamed('settingsSub', 'ThemeSettings');
const SyncSettings = lazyNamed('sync', 'SyncSettings');
const DeviceSettings = lazyNamed('device', 'DeviceSettings');

if (typeof window !== 'undefined') {
  const prefetch = () => Object.values(loaders).forEach(l => l().catch(() => {}));
  if ('requestIdleCallback' in window) window.requestIdleCallback(prefetch, { timeout: 4000 });
  else setTimeout(prefetch, 2500);
}

function PhotoDBInit() {
  React.useEffect(() => {
    if (!initPhotosDB) return;
    initPhotosDB();
    if (migratePhotosFromLocalStorage) {
      migratePhotosFromLocalStorage().catch((e) => {
        console.error('[shell] photos migration error', e);
      });
    }
  }, []);
  return null;
}

// Live theme/accent propagation (REQ-POLISH-theme-runtime, DEC-theme-accent-cssvar):
// Watches state.prefs.theme + state.prefs.accent and mirrors them onto the
// document root via applyTheme (which writes --ll-accent + data-ll-theme).
// Mounted alongside AppRouter so it runs once on first paint and every time
// the user flips a theme/accent swatch in ThemeSettings — no reload needed.
function ThemeInjector() {
  const app = useApp();
  useSyncBridge(app);
  useNativeBridge(app);
  const { state } = app;
  React.useEffect(() => {
    applyTheme(state.prefs);
  }, [state.prefs.theme, state.prefs.accent]);
  return null;
}

// Single source of truth for "is the tab bar on screen" (the toast layer and the
// live-workout bar position themselves against it).
export function tabBarVisible(state, route) {
  const needsWelcome = !state.welcomed && (state.programs || []).length === 0;
  if (needsWelcome) return false;
  return !['welcome', 'program-edit', 'exercise-edit', 'settings/', 'active-workout', 'workout-summary', 'workout-review', 'workout-preview', 'ai-coach'].some(p => route.name.startsWith(p));
}

// Desktop / tablet (≥ 900px): a side navigation rail replaces the bottom tab
// bar on every screen but first-run Welcome. Its width is published as
// --ll-rail-w (88px, 232px from 1200px) for the router and floating bars.
function railVisible(state, route) {
  const needsWelcome = !state.welcomed && (state.programs || []).length === 0;
  return !needsWelcome && route.name !== 'welcome';
}
const NAV_TABS = [
  { id: 'home',     label: 'Home',     icon: LLIcon.home },
  { id: 'progress', label: 'Progress', icon: LLIcon.trend },
  { id: 'programs', label: 'Programs', icon: LLIcon.note },
  { id: 'library',  label: 'Library',  icon: LLIcon.dumbbell },
  { id: 'settings', label: 'Settings', icon: LLIcon.settings },
];
const tabActive = (t, route) => route.name === t.id || (t.id === 'library' && route.name === 'exercise')
  || (t.id === 'programs' && route.name === 'program-edit') || (t.id === 'settings' && route.name.startsWith('settings/'));

function SideRail() {
  const { state, route, setRoute } = useApp();
  const shown = railVisible(state, route);
  React.useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('ll-has-rail', shown);
    return () => root.classList.remove('ll-has-rail');
  }, [shown]);
  if (!shown) return null;
  return (
    <nav aria-label="Main" className="ll-rail" style={{
      position: 'absolute', top: 0, bottom: 0, left: 0, width: 'var(--ll-rail-w)', zIndex: 50, boxSizing: 'border-box',
      display: 'flex', flexDirection: 'column', gap: 4, padding: 'calc(env(safe-area-inset-top, 0px) + 20px) 12px 20px',
      background: LL.bg, borderRight: `1px solid ${LL.line}`,
    }}>
      <div className="ll-rail-brand" style={{ display: 'flex', alignItems: 'center', gap: 10, height: 44, margin: '0 0 18px', padding: '0 6px' }}>
        <span style={{ width: 36, height: 36, borderRadius: LL.r.md, background: LL.accent, color: LL.accentInk, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <LLIcon.dumbbell style={{ width: 22, height: 22 }} />
        </span>
        <span className="ll-rail-text" style={{ fontFamily: LL.display, fontSize: LL.fs.h3, fontWeight: 700, color: LL.fg, letterSpacing: -0.3, whiteSpace: 'nowrap' }}>Lift Log</span>
      </div>
      {NAV_TABS.map(t => {
        const active = tabActive(t, route);
        return (
          <button key={t.id} className="ll-rail-item" aria-label={t.label} aria-current={active ? 'page' : undefined} onClick={() => setRoute(t.id)} style={{
            display: 'flex', alignItems: 'center', borderRadius: LL.r.md, border: 'none', cursor: 'pointer',
            background: active ? LL.accentSoft : 'transparent', color: active ? LL.accentText : LL.fgMute, textAlign: 'left',
            fontFamily: LL.ui, fontWeight: active ? 700 : 500,
            transition: `background ${LL.motion.fast}ms ${LL.ease}, color ${LL.motion.fast}ms ${LL.ease}`,
          }}>
            <t.icon style={{ width: 22, height: 22, flexShrink: 0 }} />
            <span className="ll-rail-label">{t.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

// Desktop layout rules. Every rule sits behind min-width, so the phone layout
// is untouched. Rail items: icon over label (88px), icon beside label (232px).
function WideStyles() {
  return (
    <style>{`
      @media (min-width: 900px) {
        :root { --ll-bottom-nav: 10px; }
        :root.ll-has-rail { --ll-rail-w: 88px; }
        .ll-rail .ll-rail-item { flex-direction: column; justify-content: center; gap: 4px; min-height: 60px; padding: 6px 0; font-size: 11px; }
        .ll-rail .ll-rail-brand { justify-content: center; padding: 0; }
        .ll-rail .ll-rail-brand .ll-rail-text { display: none; }
        .ll-rail .ll-rail-item:not([aria-current]):hover { background: var(--ll-surface, oklch(0.22 0.005 250)); color: var(--ll-fg, oklch(0.97 0.003 250)); }
        [data-ll-sheet] { max-width: 640px; margin-left: auto; margin-right: auto; }
        .ll-flow { border-left: 1px solid var(--ll-line, oklch(0.33 0.006 250)); border-right: 1px solid var(--ll-line, oklch(0.33 0.006 250)); }
      }
      @media (min-width: 1200px) {
        :root.ll-has-rail { --ll-rail-w: 232px; }
        .ll-rail .ll-rail-item { flex-direction: row; justify-content: flex-start; gap: 12px; min-height: 48px; padding: 0 12px; font-size: 15px; }
        .ll-rail .ll-rail-brand { justify-content: flex-start; padding: 0 6px; }
        .ll-rail .ll-rail-brand .ll-rail-text { display: block; }
      }
    `}</style>
  );
}

function ActiveTabBar() {
  const { state, route, setRoute } = useApp();
  const wide = useWide();
  if (wide) return <><WideStyles /><SideRail /></>;
  // Hidden during first-run Welcome and on modal/edit routes
  if (!tabBarVisible(state, route)) return null;

  const tabs = [
    { id: 'home',     label: 'Home',     icon: LLIcon.home },
    { id: 'progress', label: 'Progress', icon: LLIcon.trend },
    { id: 'programs', label: 'Programs', icon: LLIcon.note },
    { id: 'library',  label: 'Library',  icon: LLIcon.dumbbell },
    { id: 'settings', label: 'Settings', icon: LLIcon.settings },
  ];
  return (
    <div style={{
      position: 'absolute', bottom: 0, left: 0, right: 0,
      display: 'flex', padding: '6px 8px calc(env(safe-area-inset-bottom, 0px) + 8px)',
      background: `color-mix(in oklab, ${LL.bg} 88%, transparent)`, backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
      borderTop: `1px solid ${LL.line}`, zIndex: 50,
    }}>
      {tabs.map(t => {
        const active = route.name === t.id || (t.id === 'library' && route.name === 'exercise') || (t.id === 'programs' && route.name === 'program-edit');
        return (
          <button key={t.id} aria-label={t.label} aria-current={active ? 'page' : undefined} onClick={() => setRoute(t.id)} style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
            padding: '6px 0', background: 'transparent', border: 'none',
            color: active ? LL.accentText : LL.fgDim, cursor: 'pointer', minHeight: 48,
          }}>
            <span style={{ width: 52, height: 30, borderRadius: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', background: active ? LL.accentSoft : 'transparent', transition: 'background .2s' }}>
              <t.icon style={{ width: 22, height: 22 }} />
            </span>
            <span style={{ fontFamily: LL.ui, fontSize: 11, fontWeight: active ? 700 : 500 }}>{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// Toast stack. At most two toasts are visible (the store caps and coalesces).
// Placement avoids covering controls: while a sheet is open the toast drops in
// from the top (sheets own the bottom of the screen); otherwise it sits just
// above the tab bar / live-workout bar / rest dock.
function ToastStack() {
  const { state, route, runToastAction } = useApp();
  const sheetOpen = useAnySheetOpen();
  const live = state.sessions.find(s => s.id === state.activeSessionId && !s.completedAt);
  const tabs = tabBarVisible(state, route);
  const mini = !!live && route.name !== 'active-workout' && route.name !== 'welcome';
  const wide = useWide();
  let bottom = (tabs && !wide ? 80 : 20) + (mini ? 66 : 0);
  if (route.name === 'active-workout') bottom = live && live.rest && live.rest.endsAt > Date.now() ? 150 : 24;
  // With a sheet (or the keypad) open, the toast rides just above the sheet's
  // top edge instead of jumping to the top of the screen over the header.
  const [sheetTop, setSheetTop] = React.useState(null);
  const nToasts = state.toasts.length;
  React.useLayoutEffect(() => {
    if (!sheetOpen || !nToasts) { setSheetTop(null); return; }
    const measure = () => {
      let top = Infinity;
      document.querySelectorAll('[data-ll-sheet="open"]').forEach(el => {
        const host = el.offsetParent; if (!host) return;
        const t = host.getBoundingClientRect().bottom - el.offsetHeight - (parseFloat(el.style.bottom) || 0);
        if (t < top) top = t;
      });
      setSheetTop(Number.isFinite(top) ? top : null);
    };
    measure();
    const t = setTimeout(measure, 260); // after the slide-in settles
    return () => clearTimeout(t);
  }, [sheetOpen, nToasts]);
  const above = sheetOpen && sheetTop != null && sheetTop > 150;
  const pos = above
    ? { bottom: `${Math.round(window.innerHeight - sheetTop + 12)}px` }
    : sheetOpen
      ? { top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }
      : { bottom: `calc(env(safe-area-inset-bottom, 0px) + ${bottom}px)` };
  const down = sheetOpen && !above;
  // Never over a primary button: when a bottom-anchored toast would cover a
  // button marked data-ll-primary (Add exercise, Finish, Start…), lift it above.
  const stackRef = React.useRef(null);
  const [lift, setLift] = React.useState(0);
  React.useLayoutEffect(() => {
    if (down || !nToasts) { setLift(0); return undefined; }
    let raf = 0;
    const check = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const box = stackRef.current; if (!box) return;
        const r0 = box.getBoundingClientRect();
        const base = { top: r0.top + lift, bottom: r0.bottom + lift };
        let need = 0;
        document.querySelectorAll('[data-ll-primary]').forEach(b => {
          if (box.contains(b) || b.offsetParent === null || b.closest('[data-ll-sheet="open"]')) return;
          const r = b.getBoundingClientRect();
          if (r.bottom <= 0 || r.top >= window.innerHeight) return;
          if (r.top < base.bottom && r.bottom > base.top) need = Math.max(need, base.bottom - r.top + 8);
        });
        if (need !== lift) setLift(need > 0 && need < window.innerHeight * 0.5 ? need : 0);
      });
    };
    check();
    window.addEventListener('scroll', check, true);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('scroll', check, true); };
  }, [nToasts, down, bottom, sheetTop, lift]);
  // A screen can hold toasts back while it is up: the workout keypad shows them
  // in its own header and the Finish sheet stays clean ([data-ll-quiet-toasts]).
  if (sheetOpen && typeof document !== 'undefined' && document.querySelector('[data-ll-quiet-toasts]')) return null;
  return (
    <div ref={stackRef} role="status" aria-live="polite" style={{ position: 'fixed', ...pos, transform: lift ? `translateY(-${lift}px)` : undefined, transition: 'transform .2s cubic-bezier(.2,.8,.2,1)', left: wide ? 'calc(var(--ll-rail-w, 0px) + 16px)' : 16, right: 16, display: 'flex', flexDirection: down ? 'column' : 'column-reverse', alignItems: 'center', gap: 6, zIndex: 300, pointerEvents: 'none' }}>
      {state.toasts.slice(-2).map(t => (
        <div key={t.id} style={{
          padding: '6px 16px', minHeight: 40, display: 'flex', alignItems: 'center', gap: 8,
          borderRadius: LL.r.pill, maxWidth: '100%', boxSizing: 'border-box',
          background: t.tone === 'pr' ? LL.pr : LL.surface3,
          color: t.tone === 'pr' ? '#1a1200' : LL.fg,
          border: t.tone === 'pr' ? 'none' : `1px solid ${LL.line2}`,
          fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600,
          boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
          animation: `${down ? 'llToastDown' : 'llToastUp'} .22s cubic-bezier(.2,.8,.2,1)`,
        }}>
          {t.tone === 'pr' && LLIcon.trophy ? <LLIcon.trophy style={{ width: 16, height: 16, flexShrink: 0 }} /> : null}
          <span style={{ overflow: 'hidden', minWidth: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.3 }}>{t.text}</span>
          {t.action && (
            <button onClick={() => runToastAction(t.id)} style={{ pointerEvents: 'auto', marginLeft: 6, marginRight: -8, height: 32, padding: '0 12px', borderRadius: LL.r.pill, border: 'none', background: LL.accentSoft, color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>{t.action}</button>
          )}
        </div>
      ))}
      <style>{`@keyframes llToastUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}@keyframes llToastDown{from{opacity:0;transform:translateY(-10px)}to{opacity:1;transform:none}}@media (prefers-reduced-motion: reduce){[role=status]>div{animation:none!important}}`}</style>
    </div>
  );
}

// Phase 21a: no auth, no onboarding-goals, no onboarding-trial.
// First-run flow: Welcome (optional name + starter-program picker) → Home.

// Route switch
// Every screen fades/slides in on navigation. Tab roots only cross-fade.
const TAB_ROOTS = new Set(['home', 'progress', 'programs', 'library', 'settings']);
// Desktop: the dashboards (Home, Progress) and the library split use the full
// width beside the rail; every other screen is a phone-shaped flow, centered.
const WIDE_FULL = new Set(['home', 'progress', 'library', 'exercise']);
function AppRouter() {
  const { route, state } = useApp();
  const wide = useWide();
  // First run shows Welcome whatever the route: a centered flow too.
  const welcome = !state.welcomed && (state.programs || []).length === 0;
  // Desktop library: the list stays mounted while the detail pane changes.
  const split = wide && !welcome && (route.name === 'library' || route.name === 'exercise');
  const key = split ? 'library-split' : route.name + ':' + JSON.stringify(route.params || {});
  const flow = wide && (welcome || !WIDE_FULL.has(route.name));
  const body = (
    <React.Suspense fallback={<div style={{ flex: 1, background: LL.bg }} />}>
      <RouteSwitch />
    </React.Suspense>
  );
  return (
    <div key={key} style={{
      flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
      // Reserve room for the floating live-workout bar (WorkoutMiniBar sets the
      // variable while it's shown) so it never covers a screen's last actions.
      paddingBottom: 'var(--ll-mini-h, 0px)',
      ...(wide ? { marginLeft: 'var(--ll-rail-w, 0px)', position: 'relative' } : {}),
      animation: `${TAB_ROOTS.has(route.name) ? 'llFadeIn .16s' : 'llRouteIn .24s'} cubic-bezier(.2,.8,.2,1)`,
    }}>
      {flow ? (
        <div className="ll-flow" style={{ flex: 1, minHeight: 0, width: '100%', maxWidth: 720, margin: '0 auto', display: 'flex', flexDirection: 'column', position: 'relative', boxSizing: 'border-box' }}>
          {body}
        </div>
      ) : body}
    </div>
  );
}

// Desktop library: the list on the left, the selected exercise on the right.
function LibrarySplit({ exerciseId }) {
  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
      <div style={{ width: 400, flexShrink: 0, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative', borderRight: `1px solid ${LL.line}` }}>
        <ExerciseLibrary />
      </div>
      <div key={exerciseId || 'none'} style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative', animation: 'llFadeIn .16s cubic-bezier(.2,.8,.2,1)' }}>
        {exerciseId ? (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', width: '100%', maxWidth: 760, margin: '0 auto', position: 'relative' }}>
            <ExerciseDetail exerciseId={exerciseId} />
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 32, textAlign: 'center', background: LL.bg }}>
            <span style={{ width: 56, height: 56, borderRadius: LL.r.lg, background: LL.surface, border: `1px solid ${LL.line}`, color: LL.fgDim, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LLIcon.dumbbell style={{ width: 28, height: 28 }} />
            </span>
            <div style={{ fontFamily: LL.display, fontSize: LL.fs.h3, fontWeight: 700, color: LL.fg }}>Pick an exercise</div>
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, maxWidth: 300, lineHeight: 1.5 }}>Its form guide, your history, records and progress open here.</div>
          </div>
        )}
      </div>
    </div>
  );
}

function RouteSwitch() {
  const { state, route, goBack, setRoute } = useApp();
  const { name, params = {} } = route;
  const wide = useWide();

  // Phase 21a: show Welcome to a brand-new user (no programs AND not welcomed yet).
  // Users with programs or already-welcomed users bypass Welcome even if state.welcomed
  // somehow got reset (defensive).
  // A shared program link (#p=…) opens the Programs tab with its preview sheet,
  // even before onboarding. share_link.js captures the token once and strips it
  // from the URL, so closing the sheet (X, backdrop, Esc, Back) really closes it.
  const sharedPending = useSharedLinkPending();
  const needsWelcome = !state.welcomed && (state.programs || []).length === 0;
  React.useEffect(() => {
    if (sharedPending && !needsWelcome && name !== 'programs' && name !== 'settings/sync') setRoute('programs');
  }, [sharedPending]); // eslint-disable-line react-hooks/exhaustive-deps
  if (sharedPending && needsWelcome && name !== 'settings/sync') return <ProgramsScreen />;

  if (needsWelcome && name !== 'welcome' && name !== 'settings/sync') {
    return <WelcomeScreen />;
  }

  switch (name) {
    case 'welcome':      return <WelcomeScreen />;

    case 'home':         return <HomeScreen />;
    case 'programs':     return <ProgramsScreen />;
    case 'program-edit': return <ProgramEditor programId={params.programId} />;
    case 'progress':     return <InsightsScreen />;
    case 'library':      return wide ? <LibrarySplit /> : <ExerciseLibrary />;
    case 'exercise':     return wide ? <LibrarySplit exerciseId={params.exerciseId} /> : <ExerciseDetail exerciseId={params.exerciseId} />;
    case 'exercise-edit': return <ExerciseEditor exerciseId={params.exerciseId} />;
    case 'settings':     return <SettingsScreenWired />;
    case 'settings/rest':        return <RestTimerSettings />;
    case 'settings/progression': return <ProgressionSettings />;
    case 'settings/notifs':      return <NotifsSettings />;
    case 'settings/reminders':   return <RemindersSettings />;
    case 'settings/theme':    return <ThemeSettings />;
    case 'settings/advanced': return <AdvancedSettings onBack={goBack} />;
    case 'settings/export':   return <ExportScreen />;
    case 'settings/body':     return <BodyScreen />;
    case 'settings/csv':      return <CSVImport />;
    case 'settings/import-json': return <ImportJSON />;
    case 'settings/ai':       return <AISettings />;
    case 'settings/sync':     return <SyncSettings />;
    case 'settings/device':   return <DeviceSettings />;

    case 'active-workout': return <ActiveWorkout key={params.dayId || 'free'} dayId={params.dayId} freestyle={params.freestyle} retroDate={params.retroDate} />;
    case 'workout-summary': return <WorkoutSummary sessionId={params.sessionId} />;
    case 'workout-preview': return <WorkoutPreviewScreen dayId={params.dayId} date={params.date} />;
    case 'workout-review':  return <WorkoutSummary sessionId={params.sessionId} review />;

    case 'ai-coach':     return <CoachScreen />;
    // Retired routes (fake Pro/coach/health screens) and anything unknown land on Home.
    default: return <HomeScreen />;
  }
}

export { AppRouter, ActiveTabBar, ToastStack, ThemeInjector, PhotoDBInit };
